import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { ApiError, type AdminUserDetail, type IdentityUserDetail, type OrgRole, type UserSearchResult } from "../lib/api";
import { generatePassword } from "../lib/password";
import { CopyableId, CopyableEmail, CopyButton } from "./CopyableId";
import { ActiveTag, RoleTag } from "./Tags";

/**
 * Support panel for one user, merging the two backends:
 *  - Bullclip (DBCloud.WebAPI): real name, organization + role, active flag, persona, last active.
 *  - Identity server: email verification, lockout, password-vs-SSO, login providers.
 * Actions: force-verify email, clear lockout, change the organization role, enable/disable in Bullclip
 * (billing-aware, runs the seat re-sync server-side), and the admin password reset.
 *
 * `onUserChanged` (optional) fires with the fresh detail after any action that changes the Bullclip
 * side, so a list rendered above the panel can update the row in place rather than going stale.
 */
export function UserDetailPanel({
  user,
  onClose,
  onUserChanged,
}: {
  user: UserSearchResult;
  onClose: () => void;
  onUserChanged?: (detail: AdminUserDetail) => void;
}) {
  const api = useApi();

  const [bullclip, setBullclip] = useState<AdminUserDetail | null>(null);
  const [bullclipError, setBullclipError] = useState<string | null>(null);
  const [bullclipMissing, setBullclipMissing] = useState(false);
  const [identity, setIdentity] = useState<IdentityUserDetail | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [identityMissing, setIdentityMissing] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<ActionKind | null>(null);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const load = useCallback(async () => {
    setBullclip(null);
    setBullclipError(null);
    setBullclipMissing(false);
    setIdentity(null);
    setIdentityError(null);
    setIdentityMissing(false);
    setActionError(null);
    setConfirmingDisable(false);
    setConfirmingRemove(false);
    setConfirmingDelete(false);

    await Promise.all([
      api.getBullclipUser(user.userId).then(
        (d) => setBullclip(d),
        (e) => {
          if (e instanceof ApiError && e.status === 404) setBullclipMissing(true);
          else setBullclipError(e instanceof ApiError ? e.message : `${e}`);
        },
      ),
      api.getIdentityUser(user.userId).then(
        (d) => setIdentity(d),
        (e) => {
          if (e instanceof ApiError && e.status === 404) setIdentityMissing(true);
          else setIdentityError(e instanceof ApiError ? e.message : `${e}`);
        },
      ),
    ]);
  }, [api, user.userId]);

  useEffect(() => { void load(); }, [load]);

  async function runAction(kind: ActionKind, fn: () => Promise<void>) {
    setActionError(null);
    setBusyAction(kind);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : `${e}`);
    } finally {
      setBusyAction(null);
    }
  }

  // Every Bullclip-side action returns the refreshed detail: adopt it here and hand it to the list
  // above so its row stops showing the pre-action role/status.
  const applyBullclip = useCallback((detail: AdminUserDetail) => {
    setBullclip(detail);
    onUserChanged?.(detail);
  }, [onUserChanged]);

  const forceVerify = () =>
    runAction("verify", async () => setIdentity(await api.forceVerifyEmail(user.userId)));

  const unlock = () =>
    runAction("unlock", async () => setIdentity(await api.unlockUser(user.userId)));

  const setActive = (active: boolean) =>
    runAction("active", async () => {
      applyBullclip(await api.setUserActive(user.userId, active));
      setConfirmingDisable(false);
    });

  const setRole = (role: OrgRole) =>
    runAction("role", async () => applyBullclip(await api.setUserOrgRole(user.userId, role)));

  const removeFromOrg = () =>
    runAction("removeFromOrg", async () => {
      applyBullclip(await api.removeUserFromOrganization(user.userId));
      setConfirmingRemove(false);
    });

  const deleteUser = () =>
    runAction("deleteUser", async () => {
      await api.deleteUser(user.userId);
      onClose(); // the user no longer exists — close the panel
    });

  const onEmailChanged = useCallback(async (updated: AdminUserDetail) => {
    applyBullclip(updated);
    // Email also changed on the identity side — refresh that panel so it reflects the new address.
    try {
      setIdentity(await api.getIdentityUser(user.userId));
    } catch {
      /* leave the stale identity view; the change itself succeeded */
    }
  }, [api, user.userId, applyBullclip]);

  // A user is federated/SSO when they have external login providers (or no local password). Their
  // email is normally owned by the external identity provider — changing it here needs a loud warning.
  const isFederated = !!identity && (identity.providers.length > 0 || !identity.hasPassword);

  const displayName = bullclip?.displayName ?? user.displayName;

  // Type-to-confirm subject for the delete. Falls back through the search result because a user with
  // no Projects profile has no `bullclip.email` to name.
  const deleteTarget = bullclip?.email ?? user.email ?? user.userId;

  // An administrator can be removed from their organization or deleted only when nobody is stranded
  // by it: either another active administrator remains, or they are the organization's only member —
  // the expired single-seat trial support is asked to clean up, which can't be demoted either (an
  // organization must keep an administrator) and so used to be undeletable. Mirrors the server rule in
  // AdminController.DescribeAdministratorRemovalBlock so the buttons match what the endpoints accept.
  const otherOrgMembers = Math.max((bullclip?.organizationMemberCount ?? 1) - 1, 0);
  const adminActionsBlocked =
    bullclip?.hasOrganization === true && bullclip.role === "Administrator" && otherOrgMembers > 0;

  // Bullclip and the identity server share a user id. When exactly one side is missing, the user is
  // broken (can't sign in, or has no app profile/org) — surface that loudly. Both-missing can only
  // happen via a stale deep link to a since-deleted id.
  const brokenKind: "no-identity" | "no-profile" | "gone" | null =
    bullclip && identityMissing ? "no-identity"
      : identity && bullclipMissing ? "no-profile"
        : bullclipMissing && identityMissing ? "gone"
          : null;

  return (
    <section id="user-detail-panel" className="panel reveal" data-d="1" style={{ marginTop: 22 }}>
      <header>
        <span className="idx">02</span> {displayName ?? user.email ?? user.userId}
        {/* The header may be the only place the email is visible (e.g. identity-only users with
            no Projects profile) — always give it a copy affordance. */}
        {user.email && <CopyButton value={user.email} label="Copy email" />}
        <span className="hint">
          <span className="mono">{user.userId}</span>
        </span>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={onClose}>
          Close
        </button>
      </header>
      <div className="body">
        {actionError && <Err msg={actionError} />}

        {brokenKind && (
          <div className="banner-broken" role="alert">
            <div className="banner-broken-title">
              {brokenKind === "no-identity"
                ? "NO IDENTITY RECORD — USER CANNOT SIGN IN"
                : brokenKind === "no-profile"
                  ? "NO DRAWBOARD PROJECTS PROFILE — USER HAS NO APP PROFILE / ORGANIZATION"
                  : "USER NOT FOUND IN EITHER SYSTEM"}
            </div>
            <div className="banner-broken-body">
              {brokenKind === "no-identity"
                ? "This user has a Drawboard Projects profile but no identity-server account — Drawboard Projects and identity are out of sync, and the user is broken until reconciled."
                : brokenKind === "no-profile"
                  ? "This user has an identity-server account but no Drawboard Projects profile — Drawboard Projects and identity are out of sync, and the user is broken until reconciled."
                  : "Neither Drawboard Projects nor the identity server has a record for this id (it may have been deleted)."}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, alignItems: "start" }}>
          {/* ---------- Bullclip side ---------- */}
          <div>
            <SectionLabel text="Drawboard Projects" />
            {bullclipError && <Err msg={bullclipError} />}
            {bullclipMissing && (
              <div className="callout" style={{ background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
                <span className="mk">!</span>
                <span style={{ fontSize: 11 }}>
                  No Drawboard Projects profile for this user — they exist in the identity server only
                  (never finished sign-up, or were created out-of-band).
                </span>
              </div>
            )}
            {!bullclip && !bullclipError && !bullclipMissing && <Loading />}
            {bullclip && (
              <>
                <dl className="spec">
                  <dt>name</dt>
                  <dd>{bullclip.displayName ?? <span className="faint">(none)</span>}</dd>
                  <dt>email</dt>
                  <dd><CopyableEmail email={bullclip.email} /></dd>
                  <dt>persona</dt>
                  <dd>
                    {bullclip.persona === "LinkShareUser"
                      ? <span className="tag" style={{ fontSize: 10 }}>share-link user</span>
                      : <span className="mono">{bullclip.persona}</span>}
                  </dd>
                  <dt>organization</dt>
                  <dd>
                    {bullclip.hasOrganization ? (
                      <>
                        {bullclip.organizationId ? (
                          <Link
                            to={`/support/organizations?org=${encodeURIComponent(bullclip.organizationId)}`}
                            style={{ color: "var(--cyan)" }}
                            title="Open this organization"
                          >
                            {bullclip.organizationName ?? "(unnamed)"}
                          </Link>
                        ) : (
                          bullclip.organizationName ?? <span className="faint">(unnamed)</span>
                        )}
                        {bullclip.organizationId && (
                          <div style={{ marginTop: 4 }}><CopyableId value={bullclip.organizationId} /></div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: "var(--amber)" }}>none — share-link only user</span>
                    )}
                  </dd>
                  {bullclip.hasOrganization && (
                    <>
                      <dt>role</dt>
                      <dd><RoleTag role={bullclip.role} /></dd>
                    </>
                  )}
                  <dt>active</dt>
                  <dd><ActiveTag isActive={bullclip.isActive} /></dd>
                  <dt>last active</dt>
                  <dd className="mono">{fmtDate(bullclip.lastActive)}</dd>
                </dl>

                {bullclip.hasOrganization && (
                  <OrgRoleSection
                    role={bullclip.role}
                    subject={bullclip.email ?? user.userId}
                    organizationName={bullclip.organizationName}
                    busy={busyAction === "role"}
                    disabled={busyAction !== null}
                    onChange={(role) => void setRole(role)}
                  />
                )}

                {bullclip.hasOrganization && (
                  <div style={{ marginTop: 10 }}>
                    {bullclip.isActive ? (
                      !confirmingDisable ? (
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmingDisable(true)}>
                          Disable user
                        </button>
                      ) : (
                        <div className="callout warn">
                          <span className="mk">confirm</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: 10, fontSize: 11 }}>
                              Disable <b className="mono">{bullclip.email ?? user.userId}</b> in Drawboard Projects? They lose
                              access immediately. Re-enabling later re-syncs the licence seat count and can add a
                              seat to the subscription.
                            </div>
                            <div className="btn-row">
                              <button
                                className={"btn btn-danger btn-sm" + (busyAction === "active" ? " loading" : "")}
                                disabled={busyAction !== null}
                                onClick={() => void setActive(false)}
                              >
                                Confirm disable
                              </button>
                              <button className="btn btn-ghost btn-sm" disabled={busyAction !== null} onClick={() => setConfirmingDisable(false)}>
                                Back
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      <button
                        className={"btn btn-primary btn-sm" + (busyAction === "active" ? " loading" : "")}
                        disabled={busyAction !== null}
                        onClick={() => void setActive(true)}
                        title="Runs the full enable flow — re-syncs the licence seat count (can add a seat)"
                      >
                        Enable user
                      </button>
                    )}
                  </div>
                )}

                {bullclip.hasOrganization && !adminActionsBlocked && (
                  <div style={{ marginTop: 10 }}>
                    {!confirmingRemove ? (
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirmingRemove(true)}>
                        Remove from org
                      </button>
                    ) : (
                      <TypeEmailConfirm
                        confirmTarget={bullclip.email ?? user.userId}
                        confirmLabel="Remove from org"
                        busy={busyAction === "removeFromOrg"}
                        onConfirm={() => void removeFromOrg()}
                        onCancel={() => setConfirmingRemove(false)}
                        description={
                          <>
                            Remove <b className="mono">{bullclip.email ?? user.userId}</b> from{" "}
                            <b>{bullclip.organizationName ?? "their organization"}</b>? This deletes their
                            membership and all project/workspace access, converts them to a share-link
                            (guest) user, and locks sign-in until they are re-invited. Unlike disable, this
                            is not undone by re-enabling — they must be invited again.
                          </>
                        }
                      />
                    )}
                  </div>
                )}

                {/* Remove-from-org and delete stay unavailable while this administrator is the only one
                    and other members would be left unmanaged. Say so — and say what to do about it —
                    rather than leaving support staff to wonder why the buttons are missing. */}
                {adminActionsBlocked && (
                  <div className="hint" style={{ marginTop: 10 }}>
                    the only administrator of an org with {otherOrgMembers} other member
                    {otherOrgMembers === 1 ? "" : "s"} can't be removed or deleted — promote one of them
                    to administrator first
                  </div>
                )}
              </>
            )}

            {/* Delete lives outside the profile block on purpose: an identity record with no Projects
                profile is precisely the broken user support needs to remove, and the server-side flow
                skips whichever side is absent. The orphaned *login* is the thing worth deleting, so a
                missing profile only earns the button when an identity record is there to delete —
                otherwise a stale deep link to a fully deleted id would offer an action the server can
                only reject. An administrator is excluded only while other members depend on them —
                the last member of an organization strands nobody. */}
            {(bullclip || (bullclipMissing && identity)) && !adminActionsBlocked && (
              <div style={{ marginTop: 10 }}>
                {!confirmingDelete ? (
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmingDelete(true)}>
                    Delete user…
                  </button>
                ) : (
                  <TypeEmailConfirm
                    confirmTarget={deleteTarget}
                    confirmLabel="Permanently delete user"
                    busy={busyAction === "deleteUser"}
                    onConfirm={() => void deleteUser()}
                    onCancel={() => setConfirmingDelete(false)}
                    description={
                      bullclipMissing ? (
                        <>
                          <b>Permanently delete</b> <b className="mono">{deleteTarget}</b>? This user has no
                          Drawboard Projects profile, so there is nothing to anonymize — this deletes their
                          identity-server login and revokes any access it still carries, clearing the
                          orphaned record. It cannot be undone.
                        </>
                      ) : (
                        <>
                          <b>Permanently delete</b> <b className="mono">{deleteTarget}</b>? This deletes
                          their login, revokes all access, and scrubs every piece of personal data from the
                          profile (their content survives, attributed to an anonymous id) — it cannot be
                          undone. Any project they own (or workspace they solely administer) is handed to an
                          org admin first so nothing is orphaned.
                          {bullclip?.hasOrganization && bullclip.role === "Administrator" && otherOrgMembers === 0 && (
                            <>
                              {" "}
                              They are the only member of <b>{bullclip.organizationName ?? "their organization"}</b>,
                              which is left with no members — cancel its subscription in ChargeBee separately.
                            </>
                          )}
                        </>
                      )
                    }
                  />
                )}
              </div>
            )}
          </div>

          {/* ---------- Identity side ---------- */}
          <div>
            <SectionLabel text="Identity" />
            {identityError && <Err msg={identityError} />}
            {identityMissing && (
              <div className="callout" style={{ background: "var(--bg-panel-2)", border: "1px dashed var(--line-strong)" }}>
                <span className="mk">!</span>
                <span style={{ fontSize: 11 }}>
                  No identity-server account for this user — they exist in Drawboard Projects only and cannot sign in.
                </span>
              </div>
            )}
            {!identity && !identityError && !identityMissing && <Loading />}
            {identity && (
              <dl className="spec">
                <dt>email verified</dt>
                <dd>
                  {identity.emailConfirmed ? (
                    <span style={{ color: "var(--lime)" }}>verified</span>
                  ) : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "var(--amber)" }}>not verified</span>
                      <button
                        className={"btn btn-sm" + (busyAction === "verify" ? " loading" : "")}
                        disabled={busyAction !== null}
                        onClick={() => void forceVerify()}
                      >
                        Mark verified
                      </button>
                    </span>
                  )}
                </dd>
                <dt>lockout</dt>
                <dd>
                  {identity.isLockedOut ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "var(--red)" }}>{fmtLockout(identity.lockoutEnd)}</span>
                      <button
                        className={"btn btn-sm" + (busyAction === "unlock" ? " loading" : "")}
                        disabled={busyAction !== null}
                        onClick={() => void unlock()}
                      >
                        Unlock
                      </button>
                    </span>
                  ) : (
                    <span className="faint">not locked out</span>
                  )}
                </dd>
                <dt>failed attempts</dt>
                <dd className="mono">{identity.accessFailedCount}</dd>
                <dt>password</dt>
                <dd>
                  {identity.hasPassword
                    ? "has a password"
                    : <span style={{ color: "var(--cyan)" }}>SSO only — no password set</span>}
                </dd>
                <dt>providers</dt>
                <dd>
                  {identity.providers.length === 0 ? (
                    <span className="faint">none (password sign-in only)</span>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {identity.providers.map((p) => (
                        <span key={p.loginProvider} className="mono" style={{ fontSize: 11 }} title={p.loginProvider}>
                          {p.displayName}
                        </span>
                      ))}
                    </div>
                  )}
                </dd>
              </dl>
            )}
          </div>
        </div>

        {bullclip && identity && (
          <ChangeEmailSection
            userId={user.userId}
            currentEmail={bullclip.email ?? user.email}
            isFederated={isFederated}
            onChanged={onEmailChanged}
          />
        )}

        <ResetPasswordSection userId={user.userId} email={user.email} />
      </div>
    </section>
  );
}

// ---------- Organization role (Administrator ↔ Member) ----------

/**
 * Flips the user's role in their own organization. There are only two roles, so the control is a
 * single button naming the destination role rather than a picker. Both directions are confirmed:
 * promoting hands over full control of the organization, demoting takes it away — and the server
 * refuses to demote the last administrator, which surfaces as the panel's action error.
 */
function OrgRoleSection({
  role,
  subject,
  organizationName,
  busy,
  disabled,
  onChange,
}: {
  role: string | null;
  /** Email (or id) of the user being changed — used in the confirm text. */
  subject: string;
  organizationName: string | null;
  busy: boolean;
  disabled: boolean;
  onChange: (role: OrgRole) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  // Collapse once the change lands (role flips) or the panel switches to another user.
  useEffect(() => { setConfirming(false); }, [role, subject]);

  const isAdmin = role === "Administrator";
  const target: OrgRole = isAdmin ? "Member" : "Administrator";
  const label = isAdmin ? "Demote to member" : "Promote to administrator";
  const org = organizationName ?? "their organization";

  if (!confirming) {
    return (
      <div style={{ marginTop: 14 }}>
        <button className="btn btn-ghost btn-sm" disabled={disabled} onClick={() => setConfirming(true)}>
          {label}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div className="callout warn">
        <span className="mk">confirm</span>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 10, fontSize: 11 }}>
            {isAdmin ? (
              <>
                Demote <b className="mono">{subject}</b> to <b>member</b> of <b>{org}</b>? They lose
                organization administration — inviting and removing members, changing roles, and managing
                the licence and organization settings — but keep their existing project and workspace
                access. An organization must keep at least one active administrator, so this is refused
                if they are the last one.
              </>
            ) : (
              <>
                Make <b className="mono">{subject}</b> an <b>administrator</b> of <b>{org}</b>? They gain
                full control of the organization: inviting and removing members, changing roles, and
                managing the licence and organization settings. Project and workspace access and the
                licence seat count are unaffected.
              </>
            )}
          </div>
          <div className="btn-row">
            <button
              className={"btn btn-sm " + (isAdmin ? "btn-danger" : "btn-primary") + (busy ? " loading" : "")}
              disabled={disabled}
              onClick={() => onChange(target)}
            >
              {label}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirming(false)}>
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Change email (sensitive: applies instantly to Bullclip + identity, no verification) ----------

function ChangeEmailSection({
  userId,
  currentEmail,
  isFederated,
  onChanged,
}: {
  userId: string;
  currentEmail: string | null;
  isFederated: boolean;
  onChanged: (updated: AdminUserDetail) => Promise<void>;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [state, setState] = useState<{ kind: "idle" | "running" | "error" | "done"; message?: string; changedTo?: string }>({ kind: "idle" });

  // Reset when switching to a different user.
  useEffect(() => {
    setOpen(false);
    setEmail("");
    setAck(false);
    setState({ kind: "idle" });
  }, [userId]);

  const trimmed = email.trim();
  const validFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const changed = trimmed.toLowerCase() !== (currentEmail ?? "").toLowerCase();
  const canSubmit = validFormat && changed && (!isFederated || ack) && state.kind !== "running";

  async function submit() {
    setState({ kind: "running" });
    try {
      const updated = await api.changeUserEmail(userId, trimmed);
      await onChanged(updated);
      setState({ kind: "done", changedTo: trimmed });
      setOpen(false);
      setEmail("");
      setAck(false);
    } catch (e) {
      setState({ kind: "error", message: e instanceof ApiError ? e.message : `${e}` });
    }
  }

  return (
    <div style={{ marginTop: 22, borderTop: "1px dashed var(--line)", paddingTop: 16 }}>
      <SectionLabel text="Change email" />

      {state.kind === "done" && (
        <div className="callout ok" style={{ marginBottom: 12 }}>
          <span className="mk">ok</span>
          <span>
            Email changed to <b className="mono">{state.changedTo}</b>. Applied to Drawboard Projects and the
            identity server immediately — no verification email was sent.
          </span>
        </div>
      )}

      {!open ? (
        <button className="btn btn-ghost btn-sm" onClick={() => { setState({ kind: "idle" }); setOpen(true); }}>
          Change email…
        </button>
      ) : (
        <div className="callout warn">
          <span className="mk">confirm</span>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 10, fontSize: 11 }}>
              Change the email for <b className="mono">{currentEmail ?? userId}</b>. This applies <b>immediately
              with no verification email</b>, updating both Drawboard Projects (which re-points their sign-in
              lookup) and the identity server. Historical records (past invites, share-link history) keep the
              old address.
            </div>

            <div style={{ marginBottom: 6, fontSize: 11 }}>New email address:</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={currentEmail ?? "name@example.com"}
              autoComplete="off"
              spellCheck={false}
              disabled={state.kind === "running"}
              style={{ width: "100%", marginBottom: 10 }}
            />

            {trimmed.length > 0 && !validFormat && (
              <div style={{ marginBottom: 10, fontSize: 11, color: "var(--amber)" }}>
                That doesn't look like a valid email address.
              </div>
            )}
            {validFormat && !changed && (
              <div style={{ marginBottom: 10, fontSize: 11, color: "var(--amber)" }}>
                That's already this user's email.
              </div>
            )}

            {isFederated && (
              <div
                className="banner-broken"
                role="alert"
                style={{ marginBottom: 12 }}
              >
                <div className="banner-broken-title">⚠ THIS IS A FEDERATED / SSO USER</div>
                <div className="banner-broken-body">
                  This user signs in through an external identity provider (SSO). Their email is normally owned
                  by that provider — changing it here updates Drawboard Projects and the local identity record,
                  but their SSO login mapping is unaffected, so it may not change how they actually sign in and
                  can leave them out of sync with their provider. Only do this if you understand the consequences.
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 11 }}>
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={state.kind === "running"} />
                  I understand this is a federated user and want to change their email anyway.
                </label>
              </div>
            )}

            {state.kind === "error" && (
              <div className="callout error" style={{ marginBottom: 12 }}>
                <span className="mk">err</span>
                <span>{state.message}</span>
              </div>
            )}

            <div className="btn-row">
              <button
                className={"btn btn-danger btn-sm" + (state.kind === "running" ? " loading" : "")}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                Change email
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={state.kind === "running"}
                onClick={() => { setOpen(false); setEmail(""); setAck(false); setState({ kind: "idle" }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Reset password (moved here from the results table) ----------

type ResetState =
  | { kind: "idle" }
  | { kind: "editing"; password: string; confirming: boolean }
  | { kind: "running"; password: string }
  | { kind: "done"; password: string }
  | { kind: "error"; password: string; message: string };

function ResetPasswordSection({ userId, email }: { userId: string; email: string | null }) {
  const api = useApi();
  const [reset, setReset] = useState<ResetState>({ kind: "idle" });
  const [copied, setCopied] = useState(false);

  // Starting over for a different user.
  useEffect(() => {
    setReset({ kind: "idle" });
    setCopied(false);
  }, [userId]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  async function copyToClipboard(password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      const sel = window.getSelection();
      const node = document.getElementById("reset-password-value");
      if (sel && node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand("copy");
        setCopied(true);
      }
    }
  }

  async function confirmReset(password: string) {
    setReset({ kind: "running", password });
    try {
      await api.resetUserPassword(userId, password);
      setReset({ kind: "done", password });
    } catch (err) {
      setReset({ kind: "error", password, message: err instanceof ApiError ? err.message : `${err}` });
    }
  }

  const password = reset.kind === "idle" ? null : reset.password;

  return (
    <div style={{ marginTop: 22, borderTop: "1px dashed var(--line)", paddingTop: 16 }}>
      <SectionLabel text="Reset password" />

      {reset.kind === "idle" && (
        <button className="btn btn-ghost btn-sm" onClick={() => setReset({ kind: "editing", password: generatePassword(), confirming: false })}>
          Reset password…
        </button>
      )}

      {password !== null && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              border: "1px solid var(--line)",
              background: "var(--bg-panel-2)",
            }}
          >
            <code
              id="reset-password-value"
              className="mono"
              style={{ flex: 1, fontSize: 16, letterSpacing: "0.06em", userSelect: "all" }}
            >
              {password}
            </code>
            <button type="button" className="btn btn-sm" onClick={() => void copyToClipboard(password)}>
              <span className="glyph" /> {copied ? "Copied" : "Copy"}
            </button>
            {reset.kind === "editing" && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setReset({ kind: "editing", password: generatePassword(), confirming: false })}
              >
                Regenerate
              </button>
            )}
          </div>

          {reset.kind === "done" ? (
            <div className="callout ok" style={{ marginTop: 12 }}>
              <span className="mk">ok</span>
              <span>
                Password updated for <b className="mono">{email ?? userId}</b>. Share it with them through your
                existing channel — they should change it on next sign-in.
              </span>
            </div>
          ) : reset.kind === "editing" && !reset.confirming ? (
            <div className="btn-row" style={{ marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={() => setReset({ ...reset, confirming: true })}>
                <span className="glyph" /> Reset password
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setReset({ kind: "idle" })}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="callout warn" style={{ marginTop: 12 }}>
              <span className="mk">confirm</span>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 12, fontSize: 11 }}>
                  Overwrite the password for <b className="mono">{email ?? userId}</b>? The user's existing password
                  will be invalidated immediately. Make sure you've copied the new password.
                </div>
                {reset.kind === "error" && (
                  <div className="callout error" style={{ marginBottom: 12 }}>
                    <span className="mk">err</span>
                    <span>{reset.message}</span>
                  </div>
                )}
                <div className="btn-row">
                  <button
                    className={"btn btn-danger btn-sm" + (reset.kind === "running" ? " loading" : "")}
                    disabled={reset.kind === "running"}
                    onClick={() => void confirmReset(password)}
                  >
                    <span className="glyph" /> Confirm reset
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={reset.kind === "running"}
                    onClick={() => setReset({ kind: "editing", password, confirming: false })}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- shared bits ----------

/** The panel's in-flight action — at most one runs at a time, and it disables the others. */
type ActionKind = "verify" | "unlock" | "active" | "role" | "removeFromOrg" | "deleteUser";

/** Destructive-action confirm that requires typing the target's email (or id) to enable the button. */
function TypeEmailConfirm({
  confirmTarget,
  confirmLabel,
  description,
  busy,
  onConfirm,
  onCancel,
}: {
  confirmTarget: string;
  confirmLabel: string;
  description: ReactNode;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === confirmTarget.trim().toLowerCase();
  return (
    <div className="callout warn">
      <span className="mk">confirm</span>
      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: 10, fontSize: 11 }}>{description}</div>
        <div style={{ marginBottom: 6, fontSize: 11 }}>
          Type <b className="mono">{confirmTarget}</b> to confirm:
        </div>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={confirmTarget}
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          style={{ width: "100%", marginBottom: 10 }}
        />
        <div className="btn-row">
          <button
            className={"btn btn-danger btn-sm" + (busy ? " loading" : "")}
            disabled={!matches || busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div className="faint mono" style={{ fontSize: 10, letterSpacing: "0.18em", marginBottom: 10 }}>
      {text.toUpperCase()}
    </div>
  );
}

function Loading() {
  return <div className="faint mono" style={{ fontSize: 11, letterSpacing: "0.16em" }}>loading…</div>;
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="callout error" style={{ marginBottom: 12 }}>
      <span className="mk">err</span>
      <span>{msg}</span>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtLockout(lockoutEnd: string | null): string {
  if (!lockoutEnd) return "locked out";
  const d = new Date(lockoutEnd);
  // A max-value lockout end is how a permanent block is stored.
  if (isNaN(d.getTime()) || d.getFullYear() > 9000) return "locked out — permanent block";
  return `locked out until ${d.toLocaleString()}`;
}
